import type { CSSProperties, ReactNode } from 'react';
import { useState, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Bell,
  Bookmark,
  User,
  Settings,
  CircleStar,
  HeartHandshake,
  X,
  ChevronLeft,
  Gift,
  Languages,
  CircleHelp,
  GraduationCap,
  LifeBuoy,
  Menu,
  LogOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { entranceAwareCloseButtonTransition } from '@/constants/storyResourcesEntrance';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import { IconButton } from '@/components/atoms/IconButton';
import { Avatar } from '@/components/atoms/Avatar';
import { Badge } from '@/components/atoms/Badge';
import { ProgressBar } from '@/components/atoms/ProgressBar';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/molecules/Drawer';
import { formatXP } from '@/utils/formatXP';
import styles from './DashboardHeader.module.scss';
import type { DashboardHeaderProps } from './DashboardHeader.types';

const DRAWER_EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];

export function DashboardHeader({
  userName,
  userInitials,
  xp,
  notificationCount = 0,
  onNotificationClick,
  onCloseClick,
  variant,
  compactOnMobile,
  compactCloseOnDesktop,
  onlyBackHeader = false,
  compactButtonVariant = 'close',
  showInviteDrawerItem,
  onInviteClick,
  onRewardsClick,
  onXpClick,
  onLanguageClick,
  onHelpClick,
  onSavedItemsClick,
  onProfileClick,
  onSettingsClick,
  onSupportClick,
  onLevelTestClick,
  onLogout,
  onDropdownOpen,
  extendGradient,
  backdropGradientOpacity,
  className,
  progress,
}: DashboardHeaderProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const pageReady = usePageTransitionEntrance();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  // Marca activo el item del panel cuyo destino coincide con la ruta actual
  // (mismo criterio que la barra inferior: coincidencia exacta o subruta).
  const isDrawerItemActive = (path: string) =>
    pathname === path || pathname.startsWith(`${path}/`);
  const drawerItemClass = (path: string) =>
    cn(styles.header__drawerItem, isDrawerItemActive(path) && styles['header__drawerItem--active']);

  const drawerItemAnim = (index: number) => ({
    initial: { opacity: 0 },
    animate: profileDrawerOpen ? { opacity: 1 } : { opacity: 0 },
    transition: {
      duration: 0.3,
      delay: profileDrawerOpen ? 0.18 + index * 0.05 : 0,
      ease: DRAWER_EASE,
    },
  });

  type DrawerItem = { key: string; render: (anim: ReturnType<typeof drawerItemAnim>) => ReactNode };

  const notificationsItem = onNotificationClick && {
    key: 'notifications',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.NOTIFICATIONS)} aria-current={isDrawerItemActive(ROUTES.STUDENT.NOTIFICATIONS) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onNotificationClick(); }}>
        <Icon icon={Bell} size="md" />
        <span>{t('header.notifications')}</span>
        {notificationCount > 0 && (
          <Badge variant="secondary" size="sm" style={{ marginLeft: 'auto' }}>{notificationCount}</Badge>
        )}
      </motion.button>
    ),
  };

  // En móvil Invitar va siempre (equivale a la 2ª fila del menú); en escritorio
  // solo en el Dashboard, como hasta ahora.
  const inviteItem = onInviteClick && (isMobile || showInviteDrawerItem) && {
    key: 'invite',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.INVITE)} aria-current={isDrawerItemActive(ROUTES.STUDENT.INVITE) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onInviteClick(); }}>
        <Icon icon={HeartHandshake} size="md" />
        <span>{t('header.invite')}</span>
      </motion.button>
    ),
  };

  const savedItem = onSavedItemsClick && {
    key: 'saved',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.SAVED_WORDS)} aria-current={isDrawerItemActive(ROUTES.STUDENT.SAVED_WORDS) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onSavedItemsClick(); }}>
        <Icon icon={Bookmark} size="md" />
        <span>{t('header.savedItems')}</span>
      </motion.button>
    ),
  };

  const rewardsItem = onRewardsClick && {
    key: 'rewards',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.REWARDS)} aria-current={isDrawerItemActive(ROUTES.STUDENT.REWARDS) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onRewardsClick(); }}>
        <Icon icon={Gift} size="md" />
        <span>{t('header.rewards')}</span>
      </motion.button>
    ),
  };

  // Idioma y Ayuda: solo en escritorio (en móvil no van en el panel).
  const languageItem = !isMobile && onLanguageClick && {
    key: 'language',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={styles.header__drawerItem} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onLanguageClick(); }}>
        <Icon icon={Languages} size="md" />
        <span>{t('header.language')}</span>
      </motion.button>
    ),
  };

  const helpItem = !isMobile && onHelpClick && {
    key: 'help',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={styles.header__drawerItem} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onHelpClick(); }}>
        <Icon icon={CircleHelp} size="md" />
        <span>{t('header.help')}</span>
      </motion.button>
    ),
  };

  const profileItem = onProfileClick && {
    key: 'profile',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.PROFILE)} aria-current={isDrawerItemActive(ROUTES.STUDENT.PROFILE) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onProfileClick(); }}>
        <Icon icon={User} size="md" />
        <span>{t('header.profile')}</span>
      </motion.button>
    ),
  };

  const settingsItem = onSettingsClick && {
    key: 'settings',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.SETTINGS)} aria-current={isDrawerItemActive(ROUTES.STUDENT.SETTINGS) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onSettingsClick(); }}>
        <Icon icon={Settings} size="md" />
        <span>{t('header.settings')}</span>
      </motion.button>
    ),
  };

  const levelTestItem = onLevelTestClick && {
    key: 'levelTest',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.LEVEL_TEST)} aria-current={isDrawerItemActive(ROUTES.STUDENT.LEVEL_TEST) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onLevelTestClick(); }}>
        <Icon icon={GraduationCap} size="md" />
        <span>{t('header.levelTest')}</span>
      </motion.button>
    ),
  };

  const supportItem = onSupportClick && {
    key: 'support',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={drawerItemClass(ROUTES.STUDENT.SUPPORT)} aria-current={isDrawerItemActive(ROUTES.STUDENT.SUPPORT) ? 'page' : undefined} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onSupportClick(); }}>
        <Icon icon={LifeBuoy} size="md" />
        <span>{t('header.support')}</span>
      </motion.button>
    ),
  };

  const logoutItem = onLogout && {
    key: 'logout',
    render: (anim: ReturnType<typeof drawerItemAnim>) => (
      <motion.button {...anim} type="button" className={styles.header__drawerItem} data-no-press-scale="" onClick={() => { setProfileDrawerOpen(false); onLogout(); }}>
        <Icon icon={LogOut} size="md" />
        <span>{t('header.logout')}</span>
      </motion.button>
    ),
  };

  // Orden del panel: móvil = 2ª fila del menú + Notificaciones arriba, con
  // Test de nivel bajo Invitar y Soporte bajo Ajustes; escritorio = orden
  // completo de siempre (incluye Idioma/Ayuda; sin Test de nivel/Soporte por ahora).
  const drawerItems = (
    isMobile
      ? [notificationsItem, savedItem, rewardsItem, inviteItem, levelTestItem, profileItem, settingsItem, supportItem, logoutItem]
      : [notificationsItem, inviteItem, savedItem, rewardsItem, languageItem, helpItem, profileItem, settingsItem, logoutItem]
  ).filter(Boolean) as DrawerItem[];

  const backdropStyle = useMemo<CSSProperties | undefined>(
    () =>
      backdropGradientOpacity !== undefined
        ? ({
            ['--header-backdrop-opacity' as string]: String(
              Math.min(1, Math.max(0, backdropGradientOpacity))
            ),
          } as CSSProperties)
        : undefined,
    [backdropGradientOpacity]
  );

  const CloseIcon = compactButtonVariant === 'back' ? ChevronLeft : X;

  return (
    <header
      className={cn(
        styles.header,
        variant === 'contained' && styles['header--contained'],
        compactOnMobile && styles['header--compactMobile'],
        compactCloseOnDesktop &&
          onCloseClick &&
          compactButtonVariant === 'close' &&
          styles['header--compactCloseDesktop'],
        onlyBackHeader && styles['header--onlyBack'],
        extendGradient && styles['header--extendedGradient'],
        className
      )}
      style={backdropStyle}
    >
      {progress && (
        <div className={styles.header__progressWrap}>
          <ProgressBar
            value={Math.min(progress.value, progress.max)}
            max={progress.max}
            size="lg"
            className={styles.header__progressBar}
          />
        </div>
      )}

      {/* XP Badge — mobile only, left side. Pulsable → recompensas si hay onXpClick. */}
      {!onlyBackHeader && xp !== undefined && (
        <div className={styles.header__xpBadge}>
          {onXpClick ? (
            <button
              type="button"
              className={styles.header__xpButton}
              onClick={onXpClick}
              aria-label={t('header.rewards')}
            >
              <Badge variant="default" size="sm" className={styles.header__xpBadgeInner}>
                <Icon icon={CircleStar} size="sm" />
                {formatXP(xp)} {t('header.xp')}
              </Badge>
            </button>
          ) : (
            <Badge variant="default" size="sm" className={styles.header__xpBadgeInner}>
              <Icon icon={CircleStar} size="sm" />
              {formatXP(xp)} {t('header.xp')}
            </Badge>
          )}
        </div>
      )}

      {/* Close/back button — mobile only; entrada suave en layout contained (historia / fullscreen) */}
      {onCloseClick &&
        (variant === 'contained' ? (
          <motion.div
            className={cn(
              styles.header__closeBtnWrap,
              compactButtonVariant === 'back' && styles['header__closeBtnWrap--back']
            )}
            initial={{ opacity: 0, scale: 0 }}
            animate={
              pageReady ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }
            }
            transition={entranceAwareCloseButtonTransition(pageReady, Boolean(prefersReducedMotion))}
          >
            <IconButton
              icon={CloseIcon}
              variant="soft"
              onClick={onCloseClick}
              aria-label={compactButtonVariant === 'back' ? t('header.back') : t('header.close')}
            />
          </motion.div>
        ) : (
          <IconButton
            icon={CloseIcon}
            variant="soft"
            className={cn(compactButtonVariant === 'back' && styles['header__closeBtn--back'])}
            onClick={onCloseClick}
            aria-label={compactButtonVariant === 'back' ? t('header.back') : t('header.close')}
          />
        ))}

      {/* Notification Bell — escritorio (en móvil está oculto por CSS). */}
      {!onlyBackHeader && (
        <IconButton
          icon={Bell}
          className={styles.header__notificationBtn}
          onClick={onNotificationClick}
          aria-label={t('header.notifications')}
          badge={notificationCount > 0 ? <span className={styles.header__badge} /> : undefined}
        />
      )}

      {/* Avatar — escritorio: abre el drawer de usuario. Móvil: va a Perfil
          (el menú completo vive en el burger). */}
      {!onlyBackHeader && (
        <button
          type="button"
          className={styles.header__profileBtn}
          data-no-press-scale=""
          aria-label={isMobile ? t('header.profile') : t('header.userMenu')}
          onClick={() => {
            if (isMobile) {
              onProfileClick?.();
            } else {
              setProfileDrawerOpen(true);
              onDropdownOpen?.();
            }
          }}
        >
          <span className={styles.header__avatarWrapper}>
            <Avatar size="sm" fallback={userInitials} className={styles.header__avatar} />
          </span>
          <span className={styles.header__userName}>{userName}</span>
        </button>
      )}

      {/* Burger — solo móvil, a la derecha del todo: abre el panel overflow
          (mismo Drawer que escritorio). Lleva el punto de no-leídas. */}
      {!onlyBackHeader && (
        <IconButton
          icon={Menu}
          className={styles.header__menuBtn}
          onClick={() => {
            setProfileDrawerOpen(true);
            onDropdownOpen?.();
          }}
          aria-label={t('header.userMenu')}
          badge={notificationCount > 0 ? <span className={styles.header__badge} /> : undefined}
        />
      )}

      {/* Profile drawer */}
      {!onlyBackHeader && (
      <Drawer open={profileDrawerOpen} onOpenChange={setProfileDrawerOpen}>
        <DrawerContent
          side="right"
          variant="floating"
          closeSrLabel={t('header.close')}
          animateCloseButton
        >
          <div className={styles.header__drawerInner}>
              <DrawerTitle className="sr-only">{t('header.userMenu')}</DrawerTitle>

              <nav className={styles.header__drawerNav}>
                {drawerItems.map((item, i) => (
                  <div key={item.key}>
                    {item.render(drawerItemAnim(i))}
                  </div>
                ))}
              </nav>
            </div>
        </DrawerContent>
      </Drawer>
      )}
    </header>
  );
}

DashboardHeader.displayName = 'DashboardHeader';
